import { Component, ElementRef, QueryList, ViewChildren, Output, EventEmitter, PLATFORM_ID, Inject, Input, ChangeDetectorRef, NgZone } from '@angular/core';
import { FormsModule, FormGroup, Validators, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { interval, Subscription, take } from 'rxjs';
import { AuthService } from '../../shared/services/auth.service';
import { Router } from '@angular/router';
import { secureStorage } from '../../shared/services/secure-storage.service';
import { RootService } from '../../shared/services/root.service';
import { CookieService } from '../../shared/services/cookie.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RegisterComponent } from '../register/register.component';
import { TokenService } from '../../shared/services/token.service';
import { CartService } from '../../shared/services/cart.service';
import { WishlistStorageService } from '../../shared/services/wishlist-storage.service';
import { ApiService } from '../../shared/services/api.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RegisterComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  @Input() productData: any; // Accept product data from parent
  @Input() actionType: string = ''; // Accept action type (buy now, add to cart, etc.)
  @Output() loginSuccess = new EventEmitter<void>();
  verifyOtp = false;
  createForm = false;
  showLoginModal = true;
  otpForm: FormGroup;
  loginForm: FormGroup;
  otpControls = Array.from({ length: 6 }, (_, i) => `digit${i}`);
  phoneNumber = '';
  resendText = 'Resend OTP in 00:59';
  errorMessage = '';
  loading = false;
  private timerSubscription?: Subscription;
  remainingSeconds = 59;
  private sub: Subscription = new Subscription();

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private localstorage: secureStorage,
    private storageService: RootService,
    private ssrcookieService: CookieService,
    private tokenService: TokenService,
    private cartService: CartService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private wishlistStorageService: WishlistStorageService,
    private apiService: ApiService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.loginForm = this.fb.group({
      phoneNumber: ['', [Validators.required, Validators.pattern('^[6-9][0-9]{9}$')]],
      // termsAccepted: [false, [Validators.requiredTrue]]
    });

    this.otpForm = this.fb.group({
      digit0: ['', [Validators.required, Validators.pattern('^[0-9]$')]],
      digit1: ['', [Validators.required, Validators.pattern('^[0-9]$')]],
      digit2: ['', [Validators.required, Validators.pattern('^[0-9]$')]],
      digit3: ['', [Validators.required, Validators.pattern('^[0-9]$')]],
      digit4: ['', [Validators.required, Validators.pattern('^[0-9]$')]],
      digit5: ['', [Validators.required, Validators.pattern('^[0-9]$')]]
    });
  }

  isMobile: boolean = false;
  ngOnInit(): void {
    this.startResendTimer();
     if(isPlatformBrowser(this.platformId)) { 
       if (
         /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
           navigator.userAgent
         ) &&
         window.innerWidth < 480
       ) {
         this.isMobile = true;
       } else {
         this.isMobile = false;
       }
     }
  }

  ngOnDestroy() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  onInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Clear error message when user starts typing in OTP field
    if (this.errorMessage) {
      this.errorMessage = '';
    }

    // Handle paste events
    if (value.length > 1) {
      const digits = value.replace(/[^0-9]/g, '').split('');
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          this.otpForm.get(this.otpControls[index + i])?.setValue(digit);
          if (index + i + 1 < 6) {
            setTimeout(() => {
              this.otpInputs.get(index + i + 1)?.nativeElement.focus();
            }, 0);
          }
        }
      });
      return;
    }

    // Handle single digit input
    value = value.replace(/[^0-9]/g, '');
    this.otpForm.get(this.otpControls[index])?.setValue(value);

    if (value && index < 5) {
      setTimeout(() => {
        this.otpInputs.get(index + 1)?.nativeElement.focus();
      }, 0);
    }

    // Check if all digits are filled to auto-submit
    // if (this.otpForm.valid) {
    //   this.verifyOtpSubmit();
    // }
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    const input = this.otpForm.get(this.otpControls[index])?.value;

    // Clear error message when user presses backspace to clear OTP field
    if (event.key === 'Backspace' && this.errorMessage) {
      this.errorMessage = '';
    }

    if (event.key === 'Backspace' && !input && index > 0) {
      this.otpInputs.get(index - 1)?.nativeElement.focus();
    }

    // Handle Enter key press
    if (event.key === 'Enter' && this.otpForm.valid && !this.loading) {
      event.preventDefault();
      this.verifyOtpSubmit();
    }
  }

  private startResendTimer(): void {
    this.remainingSeconds = 59;
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }

    // Use setInterval inside NgZone to ensure change detection works
    const timer = setInterval(() => {
      this.ngZone.run(() => {
        this.remainingSeconds--;
        const minutes = Math.floor(this.remainingSeconds / 60);
        const seconds = this.remainingSeconds % 60;
        this.resendText = `Resend OTP in ${minutes
          .toString()
          .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

        if (this.remainingSeconds === 0) {
          this.resendText = 'Resend OTP';
          clearInterval(timer);
        }
        // Ensure UI updates immediately on each tick
        this.cdr.detectChanges();
      });
    }, 1000);

    // Store the timer reference for cleanup
    this.timerSubscription = new Subscription(() => {
      clearInterval(timer);
    });
  }

  getOtp() {
    // Mark the field as touched to trigger validation
    this.loginForm.get('phoneNumber')?.markAsTouched();
    
    // Check if the form is valid and phone number is 10 digits
    if (this.loginForm.get('phoneNumber')?.valid && 
        this.loginForm.get('phoneNumber')?.value?.length === 10 && 
        !this.loading) {
      this.loading = true;
      this.errorMessage = '';
      const phoneNumber = this.loginForm.get('phoneNumber')?.value;

      this.sub.add(this.authService.sendOtp(phoneNumber).subscribe({
        next: (response: any) => {
          this.ngZone.run(() => {
            this.loading = false;
            this.verifyOtp = true;
            this.showLoginModal = false;
            this.createForm = false;
            this.phoneNumber = phoneNumber;
            localStorage.setItem('registrationPhoneNumber', this.phoneNumber);

            this.otpForm.reset();
            this.startResendTimer();
            this.cdr.detectChanges();
            setTimeout(() => {
              this.otpInputs.first?.nativeElement.focus();
            }, 0);
          });
        },
        error: (error: any) => {
          this.ngZone.run(() => {
            this.loading = false;
            this.errorMessage = error.message || 'Failed to send OTP. Please try again.';
            this.cdr.detectChanges();
          });
        }
      }));
    }
  }

  onEditClick() {
    this.createForm = false;
    this.verifyOtp = false;
    this.showLoginModal = true;
    this.errorMessage = '';
    this.otpForm.reset();
    this.cdr.detectChanges();
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  verifyOtpSubmit() {
    if (this.otpForm.valid && !this.loading) {
      this.loading = true;
      this.errorMessage = '';

      const otp = this.otpControls
        .map(control => this.otpForm.get(control)?.value)
        .join('');
        localStorage.setItem('registrationPhoneNumber', this.phoneNumber);
        localStorage.setItem('registrationOtp', otp);


      this.sub.add(this.authService.verifyOtp(this.phoneNumber, otp, this.productData).subscribe({
        next: (response: any) => {
          this.loading = false;
          if(response.success){
        
          if (response.success && response.data?.is_active) {
            // Store tokens
            this.tokenService.storeAuthData({
              access_token: response.data.token?.access_token,
              refresh_token: response.data.token?.refresh_token,
              user_code: response.data.user_code,
              expires_in: response.data.token?.expires_in
            });
        
            // Store user data
            const userData = JSON.stringify(response.data);
            this.ssrcookieService.setItem('eol_user', userData);
            this.localstorage.setItem('eol_user', userData);
        
            // ⚠️ Consider avoiding OTP in localStorage (security risk)
            localStorage.setItem('registrationPhoneNumber', this.phoneNumber);
        
            // Update auth state
            this.authService.login(response.data);
            this.authService.refreshAuthState();
        
            // Sync local cart and wishlist
            this.syncLocalStorageCart();
            this.syncLocalWishlist();
        
            // Handle redirect based on action type
            if (['cart_payment', 'productDetail', 'buy_now'].includes(this.actionType) && response.data.first_name === null) {
              this.router.navigateByUrl('/booking-page');
            } 
            else {
              this.gotoCreateForm();
            }
        
            this.showLoginModal = false;
            this.loginSuccess.emit();
        
          } else {
            this.gotoCreateForm();
            this.cdr.detectChanges();
          }
        }
      },
      error: (error: any) => {
            this.ngZone.run(() => {
              this.loading = false;
              this.errorMessage = error.message || 'Failed to verify OTP. Please try again.';
              // Mark all OTP fields as touched to show validation errors immediately
              this.otpControls.forEach(controlName => {
                this.otpForm.get(controlName)?.markAsTouched();
              });
              // Force change detection to ensure error message displays immediately
              this.cdr.detectChanges();
            });
            // this.otpForm.reset();
            // this.otpInputs.first?.nativeElement.focus();
          }
      }));
        // next: (response: any) => {
        //   this.loading = false;
        //   if (response.success && response.data.is_active) {
        //   // Store tokens from login response first
        //   this.tokenService.storeAuthData({
        //     access_token: response?.data?.token['access_token'],
        //     refresh_token: response?.data?.token['refresh_token'],
        //     user_code: response?.data?.user_code,
        //     expires_in: response?.data?.token['expires_in']
        //   });

        //   // Store additional user data
        //   this.ssrcookieService.setItem('eol_user', JSON.stringify(response.data));
        //   this.localstorage.setItem('eol_user', JSON.stringify(response.data));
          
        //   // Store phone number and OTP for registration
        //   localStorage.setItem('registrationPhoneNumber', this.phoneNumber);
        //   localStorage.setItem('registrationOtp', otp);
          
        //   // Use auth service to update login state
        //   this.authService.login(response.data);
          
        //   // Force refresh auth state to ensure components get updated state
        //   setTimeout(() => {
        //     this.authService.refreshAuthState();
        //   }, 100);

        //   // Sync local storage cart items to server after successful login
        //   this.syncLocalStorageCart();
        //   // Only redirect if not logging in from cart or product detail
        //   if (this.actionType !== 'cart_payment' && this.actionType !== 'productDeytail'&& this.actionType !== 'buy_now') {
        //     this.router.navigateByUrl('/home');
        //   } else if (response.success && response.data.last_name){
        //     if (this.actionType === 'cart_payment' || this.actionType === 'productDeytail'|| this.actionType === 'buy_now') {
        //       this.router.navigateByUrl('/booking-page');
        //     }
        //   }
        //   this.showLoginModal = false;
        //   this.loginSuccess.emit();
        //   } else if(response.success && this.productData) {
        //     this.loginSuccess.emit();
        //     this.showLoginModal = false;
        //     // Only redirect if not logging in from cart
        //     if (this.actionType === 'cart_payment' || this.actionType === 'productDeytail'|| this.actionType === 'buy_now') {
        //       this.router.navigateByUrl('/booking-page');
        //     }
        //   } else {
        //     this.gotoCreateForm();
        //   }
        // },
      //   error: (error: any) => {
      //     this.loading = false;
      //     this.errorMessage = error.message || 'Failed to verify OTP. Please try again.';
      //     this.otpForm.reset();
      //     this.otpInputs.first?.nativeElement.focus();
      //   }
      // }));
    }
  }

  gotoCreateForm() {
    this.createForm = true;
    this.verifyOtp = false;
    this.showLoginModal = false;
    this.otpForm.reset();
    this.cdr.detectChanges();
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  resendOtp() {
    if (this.remainingSeconds === 0 && !this.loading) {
      this.getOtp();
    }
  }

  onlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  closeModal() {
    this.showLoginModal = false;
    this.verifyOtp = false;
    this.createForm = false;
    this.loginSuccess.emit();
    // No navigation needed since this is now used as a modal
  }

  // Method to sync local storage cart items to server after login
  private syncLocalStorageCart(): void {
    if (this.cartService && this.cartService.hasLocalStorageItems()) {
      this.cartService.syncLocalStorageToServer().subscribe({
        next: (res: any) => {
          console.log('Successfully synced local storage cart items:', res);
        },
        error: (err: any) => {
          console.error('Error syncing local storage cart items:', err);
        }
      });
    }
  }

  /**
   * Sync localStorage wishlist items to server after login
   */
  private syncLocalWishlist(): void {
    const wishlistItems = this.wishlistStorageService.getWishlistItemsForSync();
    
    if (wishlistItems && wishlistItems.length > 0) {
      const payload = {
        action_type: 'add_or_update',
        wishlist_items: wishlistItems
      };

      this.sub.add(
        this.apiService.addToWishlist(payload).subscribe({
          next: (res: any) => {
            console.log('Successfully synced local wishlist items:', res);
            // Clear localStorage wishlist after successful sync
            this.wishlistStorageService.clearLocalWishlist();
          },
          error: (err: any) => {
            console.error('Error syncing local wishlist items:', err);
            // Don't clear localStorage if sync fails - user can try again
          }
        })
      );
    }
  }

}
